import {
  Button,
  CloseButton,
  Flex,
  Grid,
  Heading,
  IconAddLine,
  IconEditLine,
  IconInfoSolid,
  IconTrashLine,
  List,
  Modal,
  Spinner,
  Text,
  TextInput,
  Tooltip,
} from "@instructure/ui";
import { IconButton } from "@instructure/ui-buttons";
import { t } from "i18next";
import React, { useEffect, useState } from "react";
import Skeleton from "react-loading-skeleton";
import { AttainmentLevelService } from "../../../services/AttainmentLevelService";
import { isBetaOrDev } from "../../../utils/URIUtils";
import { ERROR, useNotification } from "../../CustomHooks/NotificationProvider";
import { DialogYesNo } from "../../DialogYesNo/DialogYesNo";

export const AttainmentLevelsConfig = () => {
  const showNotification = useNotification();
  const [attainmentLevels, setAttainmentLevels] = useState([]);

  const [tempAttainmentLevels, setTempAttainmentLevels] = useState([]);

  const [open, setOpen] = useState(false);
  const [loading, isLoading] = useState(false);
  const [removeLevelDialogOpen, setRemoveLevelDialogOpen] = useState(false);
  const [removeLevelIndex, setRemoveLevelIndex] = useState(null);
  const [levelToEdit, setLevelToEdit] = useState({});
  const [levelIndexToEdit, setLevelIndexToEdit] = useState(null);
  const [levelsLoading, setLevelsLoading] = useState(false);

  useEffect(() => {
    setLevelsLoading(true);
    AttainmentLevelService.getAll().then((attainmentLevelList) => {
      attainmentLevelList.sort((o1, o2) => o1.seq - o2.seq);
      setAttainmentLevels(structuredClone(attainmentLevelList));
      setTempAttainmentLevels(structuredClone(attainmentLevelList));
      setLevelsLoading(false);
    });
  }, []);

  useEffect(() => {
    setTempAttainmentLevels(structuredClone(attainmentLevels));
  }, [attainmentLevels]);

  const addAttainmentLevel = () => {
    const newSeq =
      tempAttainmentLevels.length > 0
        ? Math.max(...tempAttainmentLevels.map((obj) => obj.seq)) + 1
        : 0;

    const newArr = [...tempAttainmentLevels];
    newArr.push({ name: "", description: "", code: "", seq: newSeq });
    setTempAttainmentLevels([...newArr]);
    handleEditClick(newArr[newArr.length - 1], newArr.length - 1);
  };

  const removeAttainmentLevel = async () => {
    attainmentLevels.splice(removeLevelIndex, 1);
    setAttainmentLevels([...attainmentLevels]);
    setRemoveLevelIndex(null);
    setRemoveLevelDialogOpen(false);
    isLoading(true);
    await AttainmentLevelService.saveAttainmentLevels({
      attainmentLevels: attainmentLevels,
    })
      .then(() => {
        isLoading(false);
        showNotification(t("notifications.attainmentLevel.deleteSuccess"));
      })
      .catch((e) => {
        showNotification(
          `${t("notifications.attainmentLevel.deleteError")} ${e}`,
          ERROR
        );
      });
  };

  const clickNoRemoveLevel = () => {
    setRemoveLevelDialogOpen(false);
  };

  const showRemoveLevelConfirmation = (index) => {
    setRemoveLevelDialogOpen(true);
    setRemoveLevelIndex(index);
  };

  const setField = (index, field, newValue) => {
    const newArr = [...tempAttainmentLevels];
    newArr[index][field] = newValue;
    setTempAttainmentLevels(newArr);
  };

  const handleEditClick = (useLevel, index) => {
    setLevelIndexToEdit(index);
    setLevelToEdit(useLevel);
    getModalBody(useLevel, index);
    setOpen(true);
  };

  const getModalBody = (level, index) => {
    return (
      <Grid colSpacing="small" rowSpacing="small" vAlign="top">
        <Grid.Row key={index}>
          <Grid.Col width={2}>
            <TextInput
              value={level.code}
              interaction={"enabled"}
              id={"att-level-code-" + index}
              renderLabel={
                <Text size="small">
                  {t("attainmentLevel.code")}{" "}
                  <Tooltip
                    renderTip={t("attainmentLevel.codeTooltip")}
                    placement="start"
                    on={["click", "hover", "focus"]}
                    color="primary-inverse"
                  >
                    <IconButton
                      size="small"
                      shape="circle"
                      renderIcon={<IconInfoSolid color="brand" />}
                      margin="0"
                      withBackground={false}
                      withBorder={false}
                      screenReaderLabel={t("global.toggleTooltip")}
                      themeOverride={{ smallHeight: "10px" }}
                    />
                  </Tooltip>
                </Text>
              }
              onChange={(e) => setField(index, "code", e.currentTarget.value)}
            />
          </Grid.Col>
          <Grid.Col width={3}>
            <TextInput
              value={level.name}
              interaction={"enabled"}
              id={"att-level-name-" + index}
              renderLabel={
                <Text size="small">{t("attainmentLevel.name")}</Text>
              }
              onChange={(e) => setField(index, "name", e.currentTarget.value)}
            />
          </Grid.Col>
          <Grid.Col>
            <TextInput
              value={level.description}
              interaction={"enabled"}
              id={"att-level-description-" + index}
              renderLabel={
                <Text size="small">{t("attainmentLevel.description")}</Text>
              }
              onChange={(e) =>
                setField(index, "description", e.currentTarget.value)
              }
            />
          </Grid.Col>
        </Grid.Row>
      </Grid>
    );
  };

  const handleSubmit = () => {
    isLoading(true);
    AttainmentLevelService.saveAttainmentLevels({
      attainmentLevels: tempAttainmentLevels,
    })
      .then(() => {
        handleClose();
        isLoading(false);
        showNotification(t("notifications.attainmentLevel.updateSuccess"));
        setAttainmentLevels(structuredClone(tempAttainmentLevels));
      })
      .catch((e) => {
        showNotification(
          `${t("notifications.attainmentLevel.updateError")} ${e}`,
          ERROR
        );
      });
  };

  const handleClose = () => {
    setOpen(false);
    setTempAttainmentLevels(structuredClone(attainmentLevels));
  };

  return (
    <>
      {isBetaOrDev(window.location.href) && (
        <Grid hAlign="end" vAlign="middle">
          <Grid.Row>
            <Grid.Col width="auto">
              <Button
                color="primary"
                onClick={addAttainmentLevel}
                renderIcon={IconAddLine}
                key={"attainment-add-button"}
                margin="0 0 small 0"
              >
                {t("attainmentLevel.addAttainmentLevelButton")}
              </Button>
            </Grid.Col>
          </Grid.Row>
        </Grid>
      )}
      {levelsLoading ? (
        <Skeleton count={3} style={{ marginBottom: 10, height: 60 }} />
      ) : (
        <>
          <List isUnstyled>
            {attainmentLevels &&
              attainmentLevels.map((level, key) => (
                <List.Item key={key}>
                  <div
                    style={{
                      backgroundColor: "#F2F8FA",
                      border: "1px solid #333",
                      borderRadius: "10px",
                      padding: "5px 15px",
                      marginBottom: 10,
                    }}
                  >
                    <Grid
                      vAlign="middle"
                      hAlign="space-between"
                      colSpacing="small"
                    >
                      <Grid.Row>
                        <Grid.Col width={10} hAlign="space-between">
                          <Flex>
                            <Flex.Item size="45px">
                              <div
                                style={{
                                  borderRadius: "50%",
                                  height: 25,
                                  width: 25,
                                  border: "1px solid #000",
                                  padding: 5,
                                  lineHeight: "25px",
                                  textAlign: "center",
                                  backgroundColor: "#0770A3",
                                  color: "#fff",
                                }}
                              >
                                {level.code}
                              </div>
                            </Flex.Item>
                            <Flex.Item shouldGrow shouldShrink>
                              <Flex direction="column">
                                <Flex.Item>
                                  <span style={{ color: " #0D323F" }}>
                                    {level.name}
                                  </span>
                                </Flex.Item>
                                <Flex.Item>
                                  <span
                                    style={{
                                      fontSize: "0.9em",
                                      color: "#156380",
                                    }}
                                  >
                                    {level.description}
                                  </span>
                                </Flex.Item>
                              </Flex>
                            </Flex.Item>
                          </Flex>
                        </Grid.Col>
                        {isBetaOrDev(window.location.href) && (
                          <Grid.Col width="auto">
                            <IconButton
                              renderIcon={IconEditLine}
                              screenReaderLabel={t(
                                "attainmentLevel.editTagScreenReaderLabel"
                              )}
                              margin="none small none none"
                              onClick={() =>
                                handleEditClick(tempAttainmentLevels[key], key)
                              }
                            />
                            <IconButton
                              renderIcon={IconTrashLine}
                              screenReaderLabel={t(
                                "attainmentLevel.deleteTagScreenReaderLabel"
                              )}
                              onClick={() => showRemoveLevelConfirmation(key)}
                            />
                          </Grid.Col>
                        )}
                      </Grid.Row>
                    </Grid>
                  </div>
                </List.Item>
              ))}
          </List>
          <DialogYesNo
            message={t("attainmentLevel.removeAttainmentLevel")}
            openStatus={removeLevelDialogOpen}
            onClickNo={clickNoRemoveLevel}
            onClickYes={removeAttainmentLevel}
          />
        </>
      )}
      <Modal
        key={"attainment-modal"}
        open={open}
        onDismiss={handleClose}
        size="large"
        label={t("attainmentLevel.configure")}
        shouldCloseOnDocumentClick
      >
        <Modal.Header>
          <Grid hAlign="space-between">
            <Grid.Row>
              <Grid.Col>
                <Heading level="h2" as="h1">
                  {t("attainmentLevel.configure")}
                </Heading>
              </Grid.Col>
              <Grid.Col width="auto">
                <CloseButton
                  size="small"
                  screenReaderLabel={t("global.close")}
                  onClick={handleClose}
                />
              </Grid.Col>
            </Grid.Row>
          </Grid>
        </Modal.Header>
        <Modal.Body>{getModalBody(levelToEdit, levelIndexToEdit)}</Modal.Body>
        <Modal.Footer>
          <Button as="button" onClick={handleClose} margin="0 small 0 0">
            {t("global.cancel")}
          </Button>

          <Button
            as="button"
            color="primary"
            type="submit"
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? (
              <Spinner renderTitle={t("global.pleaseWait")} size="x-small" />
            ) : (
              t("global.submit")
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};
